import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase, subscribeToRealtimeTopic, type RealtimeTopicEvent } from '@/lib/supabase';
import { useAuthReady } from '@/hooks/useAuthReady';

/**
 * Private "changed" topics the database pings (see get_realtime_topic):
 *   landlord — a host's own events (key = landlord id): guest actions
 *   ticket   — a support ticket's messages (key = ticket id)
 *   calendar — a unit's availability (key = unit id; public, content-free)
 *   forum    — a country's community forum (key = country code)
 */
export type RealtimeTopicKind = 'landlord' | 'ticket' | 'calendar' | 'forum';

export type RealtimeChange = Extract<RealtimeTopicEvent, { event: 'changed' }>['payload'];

/** The topic name, once the server hands it out (null until then / when refused). */
export function useRealtimeTopic(kind: RealtimeTopicKind, key: string | null | undefined): string | null {
  const { isReady } = useAuthReady();
  // Everything but the public calendar embed needs the host's login token.
  const needsLogin = kind !== 'calendar';
  const { data } = useQuery({
    queryKey: ['realtime-topic', kind, key],
    queryFn: async () => {
      const { data: topic, error } = await supabase.rpc('get_realtime_topic', { p_kind: kind, p_key: key });
      if (error) throw error;
      return typeof topic === 'string' && topic ? topic : null;
    },
    enabled: !!key && (!needsLogin || isReady),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: 2,
  });
  return data ?? null;
}

/**
 * Calls `onChange` whenever the database says something on this topic changed.
 * The ping carries no content — re-read through the normal (checked) API.
 */
export function useRealtimePing(
  kind: RealtimeTopicKind,
  key: string | null | undefined,
  onChange: (change: RealtimeChange) => void,
): void {
  const topic = useRealtimeTopic(kind, key);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!topic) return;
    return subscribeToRealtimeTopic(topic, ({ event, payload }) => {
      if (event === 'changed') onChangeRef.current(payload as RealtimeChange);
    });
  }, [topic]);
}
